<template>
  <article v-if="page" class="flex flex-row">
    <navigation-sidebar
      v-if="!hideNavigationSidebar"
      :class="[isMobile ? 'mobile-navigation-sidebar' : 'navigation-sidebar']"
      :is-mobile="isMobile"
    />
    <composite-page v-if="page.type === 'composite'" :data="page" />
    <markdown-document v-if="page.type === 'markdown'" ref="contentEl" :html="page.html" />
    <quick-link-sidebar v-if="contentEl" class="quick-link-sidebar" :root="contentEl.$el" />
  </article>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { AxiosError } from 'axios';
import Bugsnag from '@bugsnag/js';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import QuickLinkSidebar from '@/components/QuickLinkSidebar.vue';
import { useAsyncRef } from '@/utils/api';
import MarkdownDocument from '@/components/markdown/MarkdownDocument.vue';
import NavigationSidebar from '@/components/NavigationSidebar.vue';
import { ApiDataFetchingState, useApiStore } from '@/store/api';
import CompositePage from '@/components/composite/CompositePage.vue';
import { apiClient } from '../api';

const route = useRoute();
const router = useRouter();
const props = defineProps({
  id: { type: String, required: true },
  type: { type: String, required: true },
  hideNavigationSidebar: { type: Boolean, default: false },
});

const contentEl = ref(null as HTMLElement | null);

const i18n = useI18n();
const { setApiDateFetchingState } = useApiStore();
const fetchUsingApiStore = async () => {
  setApiDateFetchingState(ApiDataFetchingState.Fetching);
  try {
    const result = await apiClient.getDocumentPage(props.type, i18n.locale.value, props.id);
    setApiDateFetchingState(ApiDataFetchingState.Fetched);
    /**
     *  Added this line to resolve the Reader loading issue caused by SPA in Firefox.
     *  More Details can be found on this url - https://github.com/w3c/aria/issues/1353
     */
    await router.replace({
      path: route.path,
      hash: route.hash,
      query: { ...route.query },
      force: true,
      replace: true,
    });
    return result;
  } catch (e) {
    if (e instanceof AxiosError && e.response?.status === 404) {
      /**
       *       Support legacy URLs that include a code language at the end of the URL, e.g. /javascript
       *       Adding languages here as some languages are not there in languages.yml file
       */
      const codeLanguages = [
        'csharp',
        'dotnet',
        'curl',
        'node',
        'python',
        'ruby',
        'java',
        'php',
        'javascript',
        'kotlin',
        'swift',
        'objectiveC',
        'objective_c',
        'groovy',
      ];
      const { path } = route;
      if (codeLanguages.some(s => path.endsWith(s))) {
        await router.replace(path.substring(0, path.lastIndexOf('/')));
        return null;
      }

      setApiDateFetchingState(ApiDataFetchingState.ContentNotFound);
    } else {
      setApiDateFetchingState(ApiDataFetchingState.UnexpectedError);
      Bugsnag.notify(e as AxiosError);
    }
    throw e;
  }
};

const windowSize = ref(window.innerWidth);
const isMobile = ref(false);

function setIsMobile() {
  isMobile.value = windowSize.value <= 600;
}

onMounted(() => {
  setIsMobile();
  window.addEventListener('resize', () => {
    windowSize.value = window.innerWidth;
    setIsMobile();
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', () => {
    windowSize.value = window.innerWidth;
  });
});
const page = useAsyncRef(fetchUsingApiStore);
</script>

<style scoped lang="scss">
@media screen and (max-width: $laptop) {
  .quick-link-sidebar {
    display: none;
  }
}

@media screen and (max-width: $tablet-small) {
  .navigation-sidebar {
    display: none;
  }
  .mobile-navigation-sidebar {
    display: inline;
  }
}

@media screen and (min-width: $tablet-small) {
  .mobile-navigation-sidebar {
    display: none;
  }
}
</style>
